// File: src/features/jobs/useMcpJobManager.ts
// MCP Job Management Hook
//
// ✅ INTEGRATES: MCP capabilities with existing job management
// ✅ PROVIDES: Enhanced job operations with AI orchestration
// ✅ MAINTAINS: Compatibility with existing job management patterns

import { useState, useEffect, useCallback } from 'react';
import { useKeycloak } from '@react-keycloak/web';
import { useAppStore } from '../../store/appStore';
import {
  getJobsWithMcp,
  getJobByIdWithMcp,
  retryJobWithMcp,
  removeJobWithMcp,
  promoteJobWithMcp,
  getJobMetricsWithMcp,
  getDocumentJobMetricsWithMcp,
  getSystemMonitorWithMcp,
  getMcpHealthStatus,
  JobMcpMetrics,
  DocumentJobMcpMetrics,
  SystemMonitorMcpData,
  JobMcpOperationResult,
  McpHealthStatus
} from './mcpApi';
import { JobDetail } from './jobTypes';

// ===== INTERFACES =====

interface McpJobManagerState {
  isLoading: boolean;
  jobs: JobDetail[];
  metrics: JobMcpMetrics | null;
  documentMetrics: DocumentJobMcpMetrics | null;
  systemData: SystemMonitorMcpData | null;
  mcpHealth: McpHealthStatus | null;
  error: string | null;
  lastUpdated: string | null;
}

interface McpJobFilters {
  status?: string;
  jobType?: string;
  limit?: number;
  offset?: number;
}

interface McpJobOperations {
  // Job Management
  fetchJobs: (filters?: McpJobFilters) => Promise<void>;
  fetchJobById: (jobId: string) => Promise<JobDetail | null>;
  retryJob: (jobId: string) => Promise<JobMcpOperationResult>;
  removeJob: (jobId: string) => Promise<JobMcpOperationResult>;
  promoteJob: (jobId: string) => Promise<JobMcpOperationResult>;
  
  // Monitoring & Metrics
  fetchMetrics: () => Promise<void>;
  fetchSystemData: () => Promise<void>;
  checkMcpHealth: () => Promise<void>;
  
  
  // Utilities
  refresh: () => Promise<void>;
  clearError: () => void;
}

interface UseMcpJobManagerResult {
  state: McpJobManagerState;
  operations: McpJobOperations;
  isReady: boolean;
  canUseMcp: boolean;
}

// ===== HOOK IMPLEMENTATION =====

export function useMcpJobManager(): UseMcpJobManagerResult {
  const { keycloak } = useKeycloak();
  const { currentFirmId } = useAppStore();
  
  const [state, setState] = useState<McpJobManagerState>({
    isLoading: false,
    jobs: [],
    metrics: null,
    documentMetrics: null,
    systemData: null,
    mcpHealth: null,
    error: null,
    lastUpdated: null
  });

  // Computed states
  const isReady = Boolean(keycloak?.token && currentFirmId);
  const canUseMcp = Boolean(state.mcpHealth?.status === 'healthy');

  // ===== ERROR HANDLING =====
  
  const handleError = useCallback((error: unknown, operation: string) => {
    const errorMessage = error instanceof Error ? error.message : `${operation} failed`;
    console.error(`[useMcpJobManager] ${operation} failed:`, error);
    setState(prev => ({ ...prev, error: errorMessage, isLoading: false }));
  }, []);

  const clearError = useCallback(() => {
    setState(prev => ({ ...prev, error: null }));
  }, []);

  // ===== MCP HEALTH CHECK =====
  
  const checkMcpHealth = useCallback(async () => {
    if (!keycloak?.token) return;

    try {
      const health = await getMcpHealthStatus(keycloak.token);
      setState(prev => ({ ...prev, mcpHealth: health }));
    } catch (error) {
      // Don't set error state for health check failures
      console.warn('[useMcpJobManager] MCP health check failed:', error);
      setState(prev => ({ 
        ...prev, 
        mcpHealth: { 
          status: 'unhealthy', 
          service: 'MCP Integration',
          version: 'unknown',
          timestamp: new Date().toISOString(),
          mcpHealth: {
            initialized: false,
            mcpClient: false,
            mcpEngine: false,
            hasAiProviders: false
          },
          capabilities: {
            jobManagement: false,
            monitoring: false,
            tracing: false,
            multiAi: false
          },
          errors: [error instanceof Error ? error.message : 'Health check failed'],
          aiProviders: []
        } 
      }));
    }
  }, [keycloak?.token]);

  // ===== JOB MANAGEMENT OPERATIONS =====
  
  const fetchJobs = useCallback(async (filters: McpJobFilters = {}) => {
    if (!keycloak?.token || !currentFirmId) {
      handleError(new Error('Authentication required'), 'fetchJobs');
      return;
    }

    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      const jobs = await getJobsWithMcp({
        realmId: currentFirmId,
        ...filters
      }, keycloak.token);

      setState(prev => ({
        ...prev,
        isLoading: false,
        jobs,
        lastUpdated: new Date().toISOString()
      }));
    } catch (error) {
      handleError(error, 'fetchJobs');
    }
  }, [keycloak?.token, currentFirmId, handleError]);

  const fetchJobById = useCallback(async (jobId: string): Promise<JobDetail | null> => {
    if (!keycloak?.token) {
      handleError(new Error('Authentication required'), 'fetchJobById');
      return null;
    }

    try {
      const job = await getJobByIdWithMcp(jobId, keycloak.token);
      
      // Update the job in the jobs array if it exists
      setState(prev => ({
        ...prev,
        jobs: prev.jobs.map(j => j.id === jobId ? job : j)
      }));
      
      return job;
    } catch (error) {
      handleError(error, 'fetchJobById');
      return null;
    }
  }, [keycloak?.token, handleError]);

  const retryJob = useCallback(async (jobId: string): Promise<JobMcpOperationResult> => {
    if (!keycloak?.token || !currentFirmId) {
      const error = new Error('Authentication required');
      handleError(error, 'retryJob');
      throw error;
    }

    try {
      const result = await retryJobWithMcp(jobId, currentFirmId, keycloak.token);
      
      // Refresh the specific job if operation was successful
      if (result.success) {
        await fetchJobById(jobId);
      }
      
      return result;
    } catch (error) {
      handleError(error, 'retryJob');
      throw error;
    }
  }, [keycloak?.token, currentFirmId, fetchJobById, handleError]);

  const removeJob = useCallback(async (jobId: string): Promise<JobMcpOperationResult> => {
    if (!keycloak?.token || !currentFirmId) {
      const error = new Error('Authentication required');
      handleError(error, 'removeJob');
      throw error;
    }

    try {
      const result = await removeJobWithMcp(jobId, currentFirmId, keycloak.token);
      
      // Remove the job from the jobs array if operation was successful
      if (result.success) {
        setState(prev => ({
          ...prev,
          jobs: prev.jobs.filter(j => j.id !== jobId)
        }));
      }
      
      return result;
    } catch (error) {
      handleError(error, 'removeJob');
      throw error;
    }
  }, [keycloak?.token, currentFirmId, handleError]);

  const promoteJob = useCallback(async (jobId: string): Promise<JobMcpOperationResult> => {
    if (!keycloak?.token || !currentFirmId) {
      const error = new Error('Authentication required');
      handleError(error, 'promoteJob');
      throw error;
    }

    try {
      const result = await promoteJobWithMcp(jobId, currentFirmId, keycloak.token);
      
      // Refresh the specific job if operation was successful
      if (result.success) {
        await fetchJobById(jobId);
      }
      
      return result;
    } catch (error) {
      handleError(error, 'promoteJob');
      throw error;
    }
  }, [keycloak?.token, currentFirmId, fetchJobById, handleError]);

  // ===== MONITORING & METRICS =====
  
  const fetchMetrics = useCallback(async () => {
    if (!keycloak?.token || !currentFirmId) {
      handleError(new Error('Authentication required'), 'fetchMetrics');
      return;
    }

    try {
      const [metrics, documentMetrics] = await Promise.all([
        getJobMetricsWithMcp(currentFirmId, 'webelieve-jobs', keycloak.token),
        getDocumentJobMetricsWithMcp(currentFirmId, keycloak.token)
      ]);

      setState(prev => ({
        ...prev,
        metrics,
        documentMetrics,
        lastUpdated: new Date().toISOString()
      }));
    } catch (error) {
      handleError(error, 'fetchMetrics');
    }
  }, [keycloak?.token, currentFirmId, handleError]);

  const fetchSystemData = useCallback(async () => {
    if (!keycloak?.token || !currentFirmId) {
      handleError(new Error('Authentication required'), 'fetchSystemData');
      return;
    }

    try {
      const systemData = await getSystemMonitorWithMcp(currentFirmId, keycloak.token);
      setState(prev => ({
        ...prev,
        systemData,
        lastUpdated: new Date().toISOString()
      }));
    } catch (error) {
      handleError(error, 'fetchSystemData');
    }
  }, [keycloak?.token, currentFirmId, handleError]);

  // ===== UTILITIES =====
  
  const refresh = useCallback(async () => {
    if (!isReady) return;

    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      await Promise.all([
        checkMcpHealth(),
        fetchJobs(),
        fetchMetrics(),
        fetchSystemData()
      ]);
    } catch (error) {
      handleError(error, 'refresh');
    } finally {
      setState(prev => ({ ...prev, isLoading: false }));
    }
  }, [isReady, checkMcpHealth, fetchJobs, fetchMetrics, fetchSystemData, handleError]);

  // ===== EFFECTS =====
  
  // Initialize MCP health check on mount
  useEffect(() => {
    if (isReady) {
      checkMcpHealth();
    }
  }, [isReady, checkMcpHealth]);

  // Auto-refresh health check every 5 minutes
  useEffect(() => {
    if (!isReady) return;

    const interval = setInterval(checkMcpHealth, 300000);
    return () => clearInterval(interval);
  }, [isReady, checkMcpHealth]);

  // ===== RETURN INTERFACE =====
  
  const operations: McpJobOperations = {
    fetchJobs,
    fetchJobById,
    retryJob,
    removeJob,
    promoteJob,
    fetchMetrics,
    fetchSystemData,
    checkMcpHealth,
    refresh,
    clearError
  };

  return {
    state,
    operations,
    isReady,
    canUseMcp
  };
}

// ===== UTILITY HOOKS =====

/**
 * Hook for MCP health monitoring
 */
export function useMcpHealth() {
  const { state, operations } = useMcpJobManager();
  
  return {
    health: state.mcpHealth,
    isHealthy: state.mcpHealth?.status === 'healthy',
    checkHealth: operations.checkMcpHealth,
    capabilities: state.mcpHealth?.capabilities,
    providers: state.mcpHealth?.mcpHealth
  };
}

/**
 * Hook for MCP job metrics
 */
export function useMcpJobMetrics() {
  const { state, operations } = useMcpJobManager();
  
  return {
    metrics: state.metrics,
    documentMetrics: state.documentMetrics,
    systemData: state.systemData,
    fetchMetrics: operations.fetchMetrics,
    fetchSystemData: operations.fetchSystemData,
    isLoading: state.isLoading,
    lastUpdated: state.lastUpdated
  };
}

/**
 * Hook for MCP job operations
 */
export function useMcpJobOperations() {
  const { operations, canUseMcp } = useMcpJobManager();
  
  return {
    retryJob: operations.retryJob,
    removeJob: operations.removeJob,
    promoteJob: operations.promoteJob,
    fetchJobById: operations.fetchJobById,
    canUseMcp,
    isReady: canUseMcp
  };
}